import { useEffect, useMemo, useRef, useState } from "react";
import { Sparkles, UtensilsCrossed, Landmark, MapPin, ShieldCheck, Lightbulb, Globe2, Play, Pause, Loader2, ChevronDown, Plus, Check } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/* AiContentTabs — AI-authored guide sections for destination / airport / city pages.
   5 tabs · 46+ languages · one-tap "Listen to this section" narration.
   Language rail: top-3 recently-used languages shown as quick-switch buttons
   above the dropdown, tracked via localStorage (works for anonymous users too). */

const SECTIONS = [
  { key: "cuisine",       label: "Local Cuisine",      Icon: UtensilsCrossed },
  { key: "culture",       label: "Culture",            Icon: Landmark },
  { key: "neighborhoods", label: "Neighbourhoods",     Icon: MapPin },
  { key: "safety",        label: "Safety & Practical", Icon: ShieldCheck },
  { key: "insider_tips",  label: "Insider Tips",       Icon: Lightbulb },
];

const RECENT_LANGS_KEY = "tp_ai_recent_langs";
const MAX_RECENT = 3;

function loadRecentLangs() {
  try {
    const raw = localStorage.getItem(RECENT_LANGS_KEY);
    return raw ? JSON.parse(raw).slice(0, MAX_RECENT) : [];
  } catch { return []; }
}

function pushRecentLang(code) {
  try {
    const cur = loadRecentLangs().filter((c) => c !== code);
    const next = [code, ...cur].slice(0, MAX_RECENT);
    localStorage.setItem(RECENT_LANGS_KEY, JSON.stringify(next));
    return next;
  } catch { return [code]; }
}

/* Endpoint helper — resolves to the correct backend path per entityType. */
function contentPath(entityType, entityId) {
  if (entityType === "airport") return `/airports/${entityId}/ai-content`;
  if (entityType === "city") return `/cities/${encodeURIComponent(entityId)}/ai-content`;
  return `/destinations/${entityId}/ai-content`;
}

export default function AiContentTabs({ entityType = "destination", entityId, entityName, extraParams = {} }) {
  const [languages, setLanguages] = useState([]);
  const [lang, setLang] = useState(() => (typeof window !== "undefined" && localStorage.getItem("tp_ai_lang")) || "en");
  const [recent, setRecent] = useState(loadRecentLangs);
  const [section, setSection] = useState("cuisine");
  const [data, setData] = useState({}); // { "cuisine:en": {…} }
  const [loading, setLoading] = useState(false);
  const [ttsLoading, setTtsLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [savedKeys, setSavedKeys] = useState(new Set()); // dedupe keys already in wishlist
  const [savingKey, setSavingKey] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    api.get("/ai-content/languages")
      .then((r) => setLanguages(r.data.languages || []))
      .catch(() => setLanguages([{ code: "en", name: "English" }]));
    // Enrich recent from server profile if signed-in
    api.get("/travel/prefs").then((r) => {
      const profileLangs = r.data?.preferred_languages;
      if (Array.isArray(profileLangs) && profileLangs.length) {
        const merged = [...loadRecentLangs(), ...profileLangs].filter((v, i, a) => a.indexOf(v) === i).slice(0, MAX_RECENT);
        setRecent(merged);
        try { localStorage.setItem(RECENT_LANGS_KEY, JSON.stringify(merged)); } catch { /* ignore */ }
      }
    }).catch(() => { /* anonymous — local only, fine */ });
    // Prime the "already saved" set so the button reflects prior state
    api.get("/travel/wishlist").then((r) => {
      const keys = new Set((r.data || [])
        .filter((x) => x.entity_id === entityId && x.entity_kind === entityType)
        .map((x) => `${x.section || ""}::${x.title}`));
      setSavedKeys(keys);
    }).catch(() => { /* anonymous — button will show "Sign in to save" behaviour on click */ });
  }, [entityId, entityType]);

  const cacheKey = `${section}:${lang}`;
  const current = data[cacheKey];

  useEffect(() => {
    if (!entityId) return;
    if (data[cacheKey]) return;
    setLoading(true);
    api.get(contentPath(entityType, entityId), { params: { section, lang, ...extraParams } })
      .then((r) => setData((d) => ({ ...d, [cacheKey]: r.data })))
      .catch(() => toast.error("Couldn't load AI content — try another language or refresh"))
      .finally(() => setLoading(false));
  }, [entityType, entityId, section, lang]); // eslint-disable-line

  useEffect(() => () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } }, []);
  useEffect(() => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; setPlaying(false); }
  }, [cacheKey]);

  const changeLang = (code) => {
    setLang(code);
    try { localStorage.setItem("tp_ai_lang", code); } catch { /* ignore */ }
    setRecent(pushRecentLang(code));
  };

  const saveHighlight = async (h) => {
    const key = `${section}::${h.title}`;
    if (savedKeys.has(key) || savingKey === key) return;
    setSavingKey(key);
    try {
      await api.post("/travel/wishlist", {
        title: h.title, blurb: h.blurb || "",
        entity_kind: entityType, entity_id: String(entityId), entity_name: entityName,
        section, section_title: current?.section_title || section, tagline: current?.tagline || "", lang,
      });
      setSavedKeys((s) => new Set([...s, key]));
      toast.success(`Added "${h.title}" to My Trip Plan`);
    } catch (e) {
      const code = e?.response?.status;
      if (code === 401) toast.error("Sign in to save highlights to your trip plan");
      else toast.error("Couldn't save — try again");
    }
    setSavingKey(null);
  };

  const listen = async () => {
    if (!current?.body) return;
    if (audioRef.current && playing) { audioRef.current.pause(); setPlaying(false); return; }
    if (audioRef.current && !playing) { audioRef.current.play(); setPlaying(true); return; }
    setTtsLoading(true);
    try {
      const r = await api.post("/tts/generate", { text: `${current.section_title}. ${current.body}`, voice: "nova", speed: 1.0 });
      const audio = new Audio(`${process.env.REACT_APP_BACKEND_URL}${r.data.url}`);
      audio.onended = () => setPlaying(false);
      audio.onerror = () => { toast.error("Playback failed — try again"); setPlaying(false); };
      await audio.play();
      audioRef.current = audio;
      setPlaying(true);
    } catch { toast.error("Narration is not available right now"); }
    setTtsLoading(false);
  };

  // Recent rail: keep the currently-selected lang first; fall back to en if empty.
  const railCodes = useMemo(() => {
    const set = new Set([lang, ...(recent || [])]);
    if (set.size < 2) set.add("en");
    return Array.from(set).slice(0, MAX_RECENT);
  }, [lang, recent]);
  const nameFor = (code) => (languages.find((l) => l.code === code)?.name) || code.toUpperCase();

  return (
    <section data-testid="ai-content-tabs" className="rounded-2xl border border-amber-100 shadow-sm overflow-hidden"
      style={{ background: "linear-gradient(180deg, #faf7f0 0%, #ffffff 100%)" }}>
      <div className="p-5 sm:p-7">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.32em] text-amber-700 inline-flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> AI Travel Editor
            </div>
            <h2 className="font-serif font-800 text-3xl sm:text-4xl text-slate-900 leading-tight tracking-tight mt-1">
              A closer <span className="italic text-amber-700">look</span>
            </h2>
            <p className="text-xs text-slate-600 mt-1">
              Living guide content for {entityName} — read or listen in 46+ languages.
            </p>
          </div>
          <button onClick={listen} disabled={!current || ttsLoading} data-testid="ai-content-listen"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-40 text-white text-sm font-700 rounded-full shadow-sm">
            {ttsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            {playing ? "Pause" : "Listen to this"}
          </button>
        </div>

        {/* Language quick-switch rail — top-3 recently-used codes */}
        <div className="flex flex-wrap items-center gap-1.5 mb-4" data-testid="ai-lang-rail">
          <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-slate-500 mr-1">Language</span>
          {railCodes.map((code) => (
            <button key={code} onClick={() => changeLang(code)} data-testid={`ai-lang-rail-${code}`}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-700 border-2 transition-colors
                ${lang === code
                  ? "bg-amber-600 text-white border-amber-600 shadow-sm"
                  : "bg-white text-amber-800 border-amber-200 hover:border-amber-500"}`}>
              {nameFor(code)}
            </button>
          ))}
          <LangPicker languages={languages} value={lang} onChange={changeLang} />
        </div>

        {/* Tab bar */}
        <div className="flex flex-wrap gap-1.5 mb-6">
          {SECTIONS.map(({ key, label, Icon }) => (
            <button key={key} onClick={() => setSection(key)} data-testid={`ai-tab-${key}`}
              className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-700 border-2 transition-colors
                ${section === key
                  ? "bg-amber-600 text-white border-amber-600 shadow-sm"
                  : "bg-white text-slate-700 border-amber-200 hover:border-amber-500"}`}>
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading && !current ? (
          <div className="py-14 flex items-center justify-center text-amber-700" data-testid="ai-content-loading">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Composing your guide in {nameFor(lang)}…
          </div>
        ) : current ? (
          <div data-testid="ai-content-body">
            {current.tagline && (
              <div className="text-lg italic text-amber-700 font-serif mb-4 border-l-2 border-amber-300 pl-4">
                {current.tagline}
              </div>
            )}
            <div className="prose prose-slate max-w-none">
              {current.body?.split(/\n\n+/).map((p, i) => (
                <p key={i} className="text-[15px] text-slate-800 leading-relaxed mb-4">{p}</p>
              ))}
            </div>

            {current.highlights?.length > 0 && (
              <div className="mt-6 pt-6 border-t border-amber-100">
                <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-slate-500 mb-3">Highlights</div>
                <div className="grid sm:grid-cols-2 gap-3">
                  {current.highlights.map((h, i) => {
                    const key = `${section}::${h.title}`;
                    const isSaved = savedKeys.has(key);
                    const isSaving = savingKey === key;
                    return (
                      <div key={i} className="p-3.5 rounded-xl border border-amber-100 bg-white flex items-start justify-between gap-2" data-testid={`ai-highlight-${i}`}>
                        <div className="min-w-0 flex-1">
                          <div className="font-serif font-800 text-base text-slate-900 leading-tight">{h.title}</div>
                          <div className="text-xs text-slate-600 mt-1 leading-snug">{h.blurb}</div>
                        </div>
                        <button onClick={() => saveHighlight(h)} disabled={isSaved || isSaving}
                          data-testid={`ai-save-highlight-${i}`}
                          title={isSaved ? "Saved to My Trip Plan" : "Add to My Trip Plan"}
                          className={`shrink-0 w-8 h-8 grid place-items-center rounded-full border-2 transition-colors
                            ${isSaved ? "bg-emerald-600 border-emerald-600 text-white"
                                     : "bg-white border-amber-300 text-amber-700 hover:border-amber-500 hover:bg-amber-50"}`}>
                          {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : isSaved ? <Check className="w-3.5 h-3.5" />
                                      : <Plus className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="mt-6 text-[10px] font-mono uppercase tracking-[0.22em] text-slate-400">
              Generated by AI · cached · verify before travel
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}

function LangPicker({ languages, value, onChange }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const current = useMemo(() => languages.find((l) => l.code === value) || { code: value, name: value.toUpperCase() }, [languages, value]);
  return (
    <div className="relative" ref={rootRef}>
      <button onClick={() => setOpen((o) => !o)} data-testid="ai-lang-picker"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 border-2 border-amber-300 hover:border-amber-500 text-amber-800 text-xs font-700 rounded-full bg-white">
        <Globe2 className="w-3.5 h-3.5" /> More <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-64 max-h-72 overflow-auto rounded-xl border border-amber-200 bg-white shadow-lg z-20 py-1"
          data-testid="ai-lang-dropdown">
          {languages.map((l) => (
            <button key={l.code} onClick={() => { onChange(l.code); setOpen(false); }}
              data-testid={`ai-lang-${l.code}`}
              className={`w-full text-left px-3 py-1.5 text-sm hover:bg-amber-50 flex items-center justify-between
                ${l.code === value ? "text-amber-800 font-800 bg-amber-50/40" : "text-slate-700"}`}>
              <span>{l.name}</span>
              <span className="text-[10px] font-mono text-slate-400">{l.code}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
